import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressCard, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { CRM_NAV, TYPE_COLORS, BAND_COLORS } from './nav';

interface Contact {
    principal_type: string; principal_id: string; name: string;
    email: string | null; phone: string | null; active: boolean;
    joined: string | null; detail?: string | null;
}

const Contact360: React.FC = () => {
    const { api } = useInternalApi();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [detail, setDetail] = useState<any>(null);
    const [noteForm, setNoteForm] = useState({ kind: 'call', subject: '', body: '' });

    useEffect(() => { document.title = 'Contact 360 - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (typeFilter) params.set('type', typeFilter);
            const res = await api.get(`crm-api/contacts/?${params}`);
            if (res.data?.status === 'success') setContacts(res.data.data || []);
        } catch { setContacts([]); } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeFilter]);

    useEffect(() => { load(); }, [load]);

    const openContact = async (c: Contact) => {
        try {
            const res = await api.get(`crm-api/contacts/${c.principal_type}/${c.principal_id}/`);
            if (res.data?.status === 'success') setDetail(res.data.data);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not load contact.' });
        }
    };

    const addNote = async () => {
        if (!detail || !noteForm.subject.trim()) {
            Swal.fire({ icon: 'warning', title: 'Subject required', text: 'Give the note a subject.' });
            return;
        }
        try {
            await api.post('crm-api/notes/', {
                principal_type: detail.principal_type, principal_id: detail.principal_id,
                principal_label: detail.profile?.name || '', ...noteForm,
            });
            setNoteForm({ kind: 'call', subject: '', body: '' });
            openContact({ principal_type: detail.principal_type, principal_id: detail.principal_id } as Contact);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not add note.' });
        }
    };

    return (
        <InternalShell activeDashboard="customer_relations" subNav={CRM_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faAddressCard} className="me-2 text-primary" />Contact 360</h2>
            <p className="text-muted mb-4">Every account on the platform — patients, practitioners, institutions and clinic staff — in one place</p>

            <div className="d-flex gap-2 mb-3">
                <select className="form-select" style={{ maxWidth: 200 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">All types</option>
                    <option value="patient">Patients</option>
                    <option value="provider">Practitioners</option>
                    <option value="institution">Institutions</option>
                    <option value="staff">Clinic staff</option>
                </select>
                <div className="input-group" style={{ maxWidth: 380 }}>
                    <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                    <input className="form-control" placeholder="Search by name or email…" value={search}
                        onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
                    <button className="btn btn-outline-primary" onClick={load}>Search</button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : contacts.length === 0 ? (
                        <div className="text-center py-5 text-muted">No contacts match.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Name</th><th>Type</th><th>Email</th><th>Phone</th><th>Detail</th><th>Status</th></tr></thead>
                            <tbody>
                                {contacts.map((c, i) => (
                                    <tr key={`${c.principal_type}-${c.principal_id}-${i}`} style={{ cursor: 'pointer' }} onClick={() => openContact(c)}>
                                        <td><strong>{c.name}</strong><div className="small text-muted">{c.principal_id}</div></td>
                                        <td><span className={`badge bg-${TYPE_COLORS[c.principal_type] || 'secondary'}`}>{c.principal_type}</span></td>
                                        <td className="small">{c.email || '—'}</td>
                                        <td className="small">{c.phone || '—'}</td>
                                        <td className="small">{c.detail || '—'}</td>
                                        <td><span className={`badge ${c.active ? 'bg-success' : 'bg-secondary'}`}>{c.active ? 'Active' : 'Inactive'}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {detail && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title">{detail.profile?.name}</h5>
                                    <span className={`badge bg-${TYPE_COLORS[detail.principal_type] || 'secondary'}`}>{detail.principal_type}</span>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setDetail(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-2 mb-3 small">
                                    {Object.entries(detail.profile || {}).map(([k, v]) => (
                                        <div className="col-md-6" key={k}>
                                            <span className="text-muted">{k.replace(/_/g, ' ')}: </span>
                                            {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v as any) ?? '—'}
                                        </div>
                                    ))}
                                </div>

                                {Object.keys(detail.activity || {}).length > 0 && (
                                    <div className="d-flex gap-2 mb-3 flex-wrap">
                                        {Object.entries(detail.activity).map(([k, v]) => (
                                            <span key={k} className="badge bg-light text-dark border p-2">{k.replace(/_/g, ' ')}: {v as any}</span>
                                        ))}
                                    </div>
                                )}

                                {detail.journey && (
                                    <div className="alert alert-info py-2 small">
                                        Onboarding stage: <strong>{detail.journey.stage}</strong>
                                        {detail.journey.cohort && <> · cohort {detail.journey.cohort}</>} · {detail.journey.days_in_stage} day(s) in stage
                                    </div>
                                )}

                                {detail.feedback?.length > 0 && (
                                    <div className="mb-3">
                                        <h6 className="fw-bold">Feedback</h6>
                                        {detail.feedback.map((f: any, i: number) => (
                                            <div key={i} className="small mb-1">
                                                {f.score !== null && <span className={`badge bg-${BAND_COLORS[f.band] || 'secondary'} me-2`}>{f.score}/10</span>}
                                                {f.comment}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <h6 className="fw-bold">Interaction history</h6>
                                {detail.notes?.length === 0 ? (
                                    <p className="text-muted small">No interactions logged yet.</p>
                                ) : (
                                    <div className="mb-3" style={{ maxHeight: 200, overflowY: 'auto' }}>
                                        {detail.notes.map((n: any) => (
                                            <div key={n.id} className="border-bottom py-2">
                                                <div className="d-flex justify-content-between">
                                                    <strong className="small">{n.subject}</strong>
                                                    <span className="badge bg-light text-dark">{n.kind}</span>
                                                </div>
                                                {n.body && <div className="small">{n.body}</div>}
                                                <div className="small text-muted">{n.author || 'unknown'} · {new Date(n.created).toLocaleString()}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="card bg-light"><div className="card-body py-2">
                                    <div className="row g-2">
                                        <div className="col-md-4">
                                            <select className="form-select form-select-sm" value={noteForm.kind} onChange={(e) => setNoteForm(f => ({ ...f, kind: e.target.value }))}>
                                                <option value="call">Call</option><option value="email">Email</option>
                                                <option value="meeting">Meeting</option><option value="visit">Site visit</option>
                                                <option value="note">Internal note</option><option value="complaint">Complaint</option>
                                            </select>
                                        </div>
                                        <div className="col-md-8">
                                            <input className="form-control form-control-sm" placeholder="Subject" value={noteForm.subject}
                                                onChange={(e) => setNoteForm(f => ({ ...f, subject: e.target.value }))} />
                                        </div>
                                        <div className="col-12">
                                            <textarea className="form-control form-control-sm" rows={2} placeholder="Details (optional)" value={noteForm.body}
                                                onChange={(e) => setNoteForm(f => ({ ...f, body: e.target.value }))} />
                                        </div>
                                    </div>
                                    <button className="btn btn-sm btn-primary mt-2" onClick={addNote}>
                                        <FontAwesomeIcon icon={faPlus} className="me-1" />Log interaction
                                    </button>
                                </div></div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setDetail(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Contact360;
